import Link from 'next/link'
import { useRouter } from 'next/router'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Box, Container, Divider, Skeleton, Typography } from '@mui/material'
import Cookies from 'universal-cookie'
import ArticleAuthors from '../../components/ArticleComponents/ArticleAuthors'
import ContentGrid from '../../components/ContentComponents/ContentGrid'
import Layout from '../../components/layout'
import ReadMore from '../../components/ReadMore'
import SEOHead from '../../components/SEOHead'
import TextCTA from '../../components/TextCta'
import Topics from '../../components/Topics'
import TwoColumnCta from '../../components/TwoColumnCta'
import VideoPlaylist from '../../components/VideoComponents/VideoPlaylist'
import { options } from '../../const'
import { WITSBY_PAYWALL_COOKIE } from '../../const/cookie'
import { hasAccessToPageOrWebinar } from '../../lib/access-validator'
import { getContentfulDirectClient } from '../../lib/apollo-client'
import {
  contentfulImageTransformation,
  contentfulAPIToSEOHead,
} from '../../lib/data-transformations'
import useUserAccount from '../../lib/hooks/useUserAccount'
import GET_WEBINAR from '../../lib/schema/pages/getWebinar.graphql'
import GET_WEBINAR_LIST_QUERY from '../../lib/schema/pages/getWebinarList.graphql'
import paths from '../../paths/path'

export default function Webinar({
  topicSecondary,
  featuredBook,
  seo,
  videoId,
  buttonLinkComponent,
  registrationRecordingUrl,
  title,
  topic,
  premium,
  memberAuthentication,
  date,
  endDatetime,
  description,
  authors,
  webinars,
}) {
  const router = useRouter()
  const { user, userAccountAccess } = useUserAccount()

  const cookies = new Cookies()
  const witsbyPaywallCookie = cookies.get(WITSBY_PAYWALL_COOKIE)

  if (router.isFallback) {
    return (
      <Skeleton
        animation='wave'
        variant='rectangular'
        width='100%'
        height='100px'
      />
    )
  }

  const secondaryTopics =
    topicSecondary &&
    topicSecondary.items.reduce((unique, o) => {
      if (!unique.includes(o)) {
        unique.push(o)
      }
      return unique
    }, [])

  const topics = [...secondaryTopics, topic]

  const latestWebinars = [...webinars].sort((a, b) => {
    return new Date(b.date) - new Date(a.date)
  })

  const relatedWebinars = latestWebinars.filter(
    (item) => item?.topic?.title === topic?.title
  )

  const book = featuredBook

  const url = '#'
  const handleLoginClick = () => {
    const returnUrl = window.location.href
    window.location.href = `${process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_URL}?returnUrl=${returnUrl}`
    sessionStorage.setItem('returnUrl', returnUrl)
  }
  const membership = (memberauthentication) => {
    if (memberauthentication?.length) {
      return memberauthentication?.split(' ')[0]
    }
  }
  const hasPageOrWebinarAccess = hasAccessToPageOrWebinar(
    membership(memberAuthentication),
    userAccountAccess
  )

  return (
    <Layout>
      <SEOHead seo={seo} />
      {(!!witsbyPaywallCookie ||
        !memberAuthentication ||
        memberAuthentication === 'None' ||
        (memberAuthentication === 'Registered User' && user?.authenticated) ||
        ((memberAuthentication === 'Basic Member' ||
          memberAuthentication === 'Select Member' ||
          memberAuthentication === 'Premium Member') &&
          user?.authenticated &&
          hasPageOrWebinarAccess)) && (
        <Container>
          <Box pb={3} mt={2}>
            <VideoPlaylist
              topic={topic}
              videoId={videoId}
              buttonLinkComponent={buttonLinkComponent}
              registrationRecordingUrl={registrationRecordingUrl}
              thumbnail={seo.thumbnail}
              premium={premium}
              title={title}
              date={date}
              endDatetime={endDatetime}
              videos={relatedWebinars}
              type='webinar'
            />
          </Box>
        </Container>
      )}
      {(!!witsbyPaywallCookie ||
        !memberAuthentication ||
        memberAuthentication === 'None' ||
        (memberAuthentication === 'Registered User' && user?.authenticated) ||
        ((memberAuthentication === 'Basic Member' ||
          memberAuthentication === 'Select Member' ||
          memberAuthentication === 'Premium Member') &&
          user?.authenticated &&
          hasPageOrWebinarAccess)) && (
        <Box mb={[0, 10]}>
          <Divider />
        </Box>
      )}
      {(!!witsbyPaywallCookie ||
        !memberAuthentication ||
        memberAuthentication === 'None' ||
        (memberAuthentication === 'Registered User' && user?.authenticated) ||
        ((memberAuthentication === 'Basic Member' ||
          memberAuthentication === 'Select Member' ||
          memberAuthentication === 'Premium Member') &&
          user?.authenticated &&
          hasPageOrWebinarAccess)) && (
        <Container>
          <Box maxWidth={850}>
            {description?.json && (
              <Box mt={[5, 0]} id='about'>
                <ReadMore
                  title='In this Webinar'
                  titleVariant='h4'
                  short={description?.json}
                  textAlign='left'
                  hideSummaryWhenExpanded
                />
              </Box>
            )}
            {authors.items && authors.items.length > 0 && (
              <Box mt={[5, 9]} id='webinar-presenter'>
                <ArticleAuthors
                  authors={authors.items}
                  title='About the presenter'
                />
              </Box>
            )}
            {!!topics?.length && (
              <Box mt={6} id='topics'>
                <Topics
                  title='Topics covered'
                  titleVariant='h4'
                  topics={topics}
                  contentType='webinar'
                  variant='basicSmall'
                />
              </Box>
            )}
          </Box>
          {book && (
            <Box my={11}>
              <TwoColumnCta
                testId='webinar-book-2-col-cta'
                title={book.title}
                description={
                  documentToReactComponents(
                    book?.description?.json,
                    options()
                  )[0]
                }
                ctaItems={[
                  {
                    __typename: 'ButtonLinkComponent',
                    linkUrl: paths.book({ slug: book?.slug }),
                    linkLabel: 'Shop',
                  },
                ]}
                label={book.topic?.title}
                image={contentfulImageTransformation(book.featuredImage)}
                imagePos='right'
                variant='grey'
              />
            </Box>
          )}
          <Box mt={11}>
            <ContentGrid
              sectionTitle='Latest Webinars'
              items={latestWebinars}
              viewAllLink={paths.search({ types: ['webinar'] })}
            />
          </Box>
          <Box my={9}>
            <TextCTA
              title='Learn and Connect With Peers'
              description='Join us for one of our upcoming live events to continue learning while having the opportunity to connect with educators from around the world!'
              ctaLabel='Upcoming Events'
              ctaLink={paths.events}
            />
          </Box>
        </Container>
      )}
      {(memberAuthentication === 'Registered User' ||
        memberAuthentication === 'Basic Member' ||
        memberAuthentication === 'Select Member' ||
        memberAuthentication === 'Premium Member') &&
        !witsbyPaywallCookie &&
        !user?.authenticated && (
          <Container maxWidth='lg'>
            <Box mt={[15, 20]} mb={[6, 10]}>
              <Typography>
                This content is a benefit of one or more of our membership
                plans. Please{' '}
                <a
                  href={url}
                  onClick={handleLoginClick}
                  style={{ textDecoration: 'underline' }}
                >
                  log in
                </a>{' '}
                to your account to view it, or visit our{' '}
                <Link href='/memberships'>
                  <a style={{ textDecoration: 'underline' }}>membership page</a>
                </Link>{' '}
                to explore your options.
              </Typography>
            </Box>
          </Container>
        )}
      {(memberAuthentication === 'Basic Member' ||
        memberAuthentication === 'Select Member' ||
        memberAuthentication === 'Premium Member') &&
        user?.authenticated &&
        !witsbyPaywallCookie &&
        !hasPageOrWebinarAccess && (
          <Container maxWidth='lg'>
            <Box mt={[15, 20]} mb={[6, 10]}>
              <Typography>
                You do not have access to this content. Please{' '}
                <Link href='/contact'>
                  <a style={{ textDecoration: 'underline' }}>contact us</a>
                </Link>{' '}
                for assistance if you believe this to be incorrect, or visit our{' '}
                <Link href='/memberships'>
                  <a style={{ textDecoration: 'underline' }}>membership page</a>
                </Link>{' '}
                to explore your options.
              </Typography>
            </Box>
          </Container>
        )}
    </Layout>
  )
}

export async function getStaticPaths() {
  const contentfulDirectClient = await getContentfulDirectClient()
  const {
    data: { webinars: webinarData },
  } = await contentfulDirectClient.query({
    query: GET_WEBINAR_LIST_QUERY,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: Number(process.env.NEXT_STATIC_BUILD_LIMIT || 200),
    },
  })

  return {
    paths: webinarData?.items
      ?.filter((item) => !!item.slug)
      ?.map((item) => ({
        params: { webinar: item.slug },
      })),
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  const contentfulDirectClient = await getContentfulDirectClient()
  const {
    data: { webinar, webinars },
  } = await contentfulDirectClient.query({
    query: GET_WEBINAR,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      whereWebinar: { slug: params?.webinar },
      whereWebinars: { slug_not: params?.webinar },
      webinarLimit: 1,
      webinarsLimit: 100,
    },
  })

  if (!webinar || !webinar.items?.length) {
    return {
      notFound: true,
    }
  }

  const {
    topicSecondary,
    featuredBook,
    seo,
    videoId,
    buttonLink,
    registrationRecordingUrl,
    title,
    topic,
    premium,
    memberAuthentication,
    date,
    endDatetime,
    description,
    authors,
    thumbnail,
    sys,
    __typename,
  } = webinar.items?.[0]

  return {
    props: {
      topicSecondary,
      featuredBook,
      seo:
        seo ||
        contentfulAPIToSEOHead({
          title,
          description: description?.json,
          thumbnail,
          sys,
          __typename,
        }),
      videoId,
      buttonLinkComponent: buttonLink,
      registrationRecordingUrl,
      title,
      topic,
      premium,
      memberAuthentication,
      date,
      endDatetime,
      description,
      authors,
      webinars: webinars.items || [],
    },
    revalidate: 20,
  }
}
